import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useExportIglooStaffKeyLogs } from '@/api/keyless/igloo/export-igloo-staff-key-logs'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { ErrorMessage } from '@/components/ui/error-message'
import { Label } from '@/components/ui/label'
import { toastFactory } from '@/factories'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { BuildingFilterItem } from '@/reducers/types'
import { selectUserRole } from '@/reducers/userDataReducer'
import { parseLocalDate } from '@/utils/date/parse-local-date'
import { buildCsvFilename } from '@/utils/file/build-csv-filename'
import { downloadCsv } from '@/utils/file/download-csv'

import {
  IGLOO_LOGS_END_DATE_ID,
  IGLOO_LOGS_FILE_PAGE,
  IGLOO_LOGS_START_DATE_ID,
} from '../igloo-logs.constants'
import {
  canDownloadIglooLogs,
  getIglooLogsBounds,
  getIglooLogsErrorMessage,
  getIglooLogsValidationMessage,
  getPropertyToday,
  resolveIglooLogsRange,
} from '../igloo-logs.utils'
import * as S from './download-igloo-logs.styled'

type DownloadIglooLogsFormProps = {
  building: BuildingFilterItem
}

const DownloadIglooLogsForm = ({ building }: DownloadIglooLogsFormProps) => {
  const { t } = useTranslation(['management-users'])
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [isEmpty, setIsEmpty] = useState(false)
  const { isPending, mutate } = useExportIglooStaffKeyLogs()
  const isRequestingRef = useRef(false)

  const today = getPropertyToday(building.timezone, new Date())
  const bounds = getIglooLogsBounds({ endDate, startDate, today })
  const resolution = resolveIglooLogsRange({ endDate, startDate, today })
  const validationError = 'error' in resolution ? resolution.error : null

  const handleStartDateChange = (date: Date | null) => {
    setIsEmpty(false)
    setStartDate(date)
  }

  const handleEndDateChange = (date: Date | null) => {
    setIsEmpty(false)
    setEndDate(date)
  }

  const handleDownload = () => {
    if (isRequestingRef.current || !('range' in resolution)) return
    const { range } = resolution
    isRequestingRef.current = true
    setIsEmpty(false)
    mutate(
      { buildingId: building.id, ...range },
      {
        onError: (error) => {
          const { errorCode, response } = error as {
            errorCode?: string
            response?: { status?: number }
          }
          toastFactory.error(getIglooLogsErrorMessage(t, errorCode, response?.status))
        },
        onSettled: () => {
          isRequestingRef.current = false
        },
        onSuccess: (blob) => {
          if (!blob) {
            setIsEmpty(true)
            return
          }
          downloadCsv({
            blob,
            filename: buildCsvFilename({
              building: building.name,
              dateRange: [parseLocalDate(range.startDate), parseLocalDate(range.endDate)],
              page: IGLOO_LOGS_FILE_PAGE,
            }),
          })
        },
      },
    )
  }

  return (
    <S.Container>
      <S.Title color="black" forwardedAs="span" size="unset" weight="semibold" whiteSpace="nowrap">
        {t('management-users:iglooLogs.title')}
      </S.Title>
      <S.FieldLabel $area="startLabel">
        <Label htmlFor={IGLOO_LOGS_START_DATE_ID} size="small">
          {t('management-users:iglooLogs.startDate.label')}
        </Label>
      </S.FieldLabel>
      <S.Field $area="start">
        <DatePickerInput
          id={IGLOO_LOGS_START_DATE_ID}
          maxDate={bounds.start.max}
          minDate={bounds.start.min}
          name="startDate"
          placeholder={t('management-users:iglooLogs.startDate.placeholder')}
          size="small"
          value={startDate}
          onChange={handleStartDateChange}
        />
      </S.Field>
      <S.FieldLabel $area="endLabel">
        <Label htmlFor={IGLOO_LOGS_END_DATE_ID} size="small">
          {t('management-users:iglooLogs.endDate.label')}
        </Label>
      </S.FieldLabel>
      <S.Field $area="end">
        <DatePickerInput
          id={IGLOO_LOGS_END_DATE_ID}
          maxDate={bounds.end.max}
          minDate={bounds.end.min}
          name="endDate"
          placeholder={t('management-users:iglooLogs.endDate.placeholder')}
          size="small"
          value={endDate}
          onChange={handleEndDateChange}
        />
      </S.Field>
      <S.Message>
        <ErrorMessage>
          {validationError && getIglooLogsValidationMessage(t, validationError)}
        </ErrorMessage>
        <ErrorMessage>{isEmpty && t('management-users:iglooLogs.empty')}</ErrorMessage>
      </S.Message>
      <S.DownloadButton
        disabled={!!validationError}
        loading={isPending}
        size="medium"
        use="primary"
        onClick={handleDownload}
      >
        {t('management-users:iglooLogs.download')}
      </S.DownloadButton>
    </S.Container>
  )
}

export const DownloadIglooLogs = () => {
  const role = useSelector(selectUserRole)
  const activeBuilding = useSelector(selectActiveBuilding)

  if (!activeBuilding || !canDownloadIglooLogs(role, activeBuilding)) return null

  return <DownloadIglooLogsForm key={activeBuilding.id} building={activeBuilding} />
}
